import { Stripe } from '@stripe/stripe-js';
import { Reader, Terminal } from '@stripe/terminal-js';
import { UserWorkoutSession } from 'fitbeat.gym-core';
import {
  IUserExperiencePointsInstanceViewModel,
  ICancelSubscriptionResponse,
  IInvoice,
  IPaymentSetupData,
  IPhaseActionInfo,
  IPrice,
  IStripeCouponWrapper,
  ISubscriptionPaymentSummary,
  UserAccountScheduleActionInfo,
} from 'fitbeat.models';
import moment from 'moment';

export interface IStartSubscription {
  plan: IPrice | undefined;
  couponsForPlan: IStripeCouponWrapper[];
  coupon: IStripeCouponWrapper | undefined;
  couponChecked: boolean;
  consentChecked: boolean;
  stripe: Stripe | null;
  couponPlaceholder: string;
  isLoadingCoupons: boolean;
  paymentSummary: ISubscriptionPaymentSummary | undefined;
}

export type ITerminalStatus = 'setting_up' | 'scanning' | 'connecting' | 'disconnected' | 'ready' |
  'capturing' | 'processing_card_capture' | 'processing_payment';

export interface IBuyMerchandise {
  merchandise: IPrice[];
  invoice: IInvoice | undefined;
  error: string | null;
  isLoadingInvoice: boolean;
  coupons: IStripeCouponWrapper[] | undefined;
  selectedCoupon: IStripeCouponWrapper | undefined;
  isProcessingPayment: boolean;
  success: string | undefined;
  stripe: Stripe | null;
  note: string | undefined;
  showConfirmActionInvoiceModal: boolean;
  showMerchandiseSuccessModal: boolean;
}
export interface IMemberAccountActionsData {
    memberAccountShedule: UserAccountScheduleActionInfo;
    cancelSubscriptionDetail?: ICancelSubscriptionResponse;
    memberWorkoutHistory: UserWorkoutSession[];
    memberExperiencePointsHistory: IUserExperiencePointsInstanceViewModel[];
    startSubscription: IStartSubscription;
    buyMerchandise: IBuyMerchandise;
    setupIntent: IPaymentSetupData | undefined;
    terminal: Terminal | null;
    readers: Reader[];
    connectedReader: Reader | null;
}
export interface IMemberAccountActionsState {
    data: IMemberAccountActionsData;
    view: IMemberAccountActionsUIView;
}

export interface IMemberAccountActionsUIView {
    terminalStatus: ITerminalStatus;
    error: null | string;
    isFetching: boolean;
    isFetchingExperiencePointsHistory: boolean;
    experiencePointsHistoryError: null | string;
    isLoadingTerminal: boolean;
    terminalError: string  | null;
    showAddPauseModal: boolean;
    showAddFreeWeeksModal: boolean;
    addFreezeInfo?: IPhaseActionInfo;
    addFreeWeekInfo?: IPhaseActionInfo;
    showCancelSubscriptionModal: boolean;
    showStartSubscriptionModal: boolean;
    showStartSubscriptionSuccessModal: boolean;
    showBuyMerchandiseModal: boolean;
    isLoadingBuyMerchandiseModal: boolean;
    validationError: null | string;
    successMessage: null | string;
    showCancelPauseModal: boolean;
    pauseEarliestEnd: string;
    pausePlanId: string;
    pauseStartDate: string;
    pauseEndDate: string;
    allowedPlanIds?: Array<{name: string | null, price: string, accountId: string}>;
    allowedFreeWeekPlanIds?: Array<{name: string, price: string, accountId: string}>;
    allowedStartSubscriptionPlans?: IPrice[];
    isLoadingCancelSubscription: boolean;
    isLoadingStartSubscription: boolean;
    cancelSubscriptionError: null | string;
    startSubscriptionError: null | string;
    freeWeekEarliestEnd: string;
    freeWeekPlanId: string;
    freeWeekStartDate: string;
    freeWeekEndDate: string;
}

export const initialState: IMemberAccountActionsState = {
    data: {
      terminal: null,
      readers: [],
      connectedReader: null,
        memberAccountShedule: {
            cancelSubscriptionInfo: {
                date: new Date().toISOString(),
                enabled: false,
                defaultPenaltyAmount: 0,
                message: 'Are you sure you want to cancel?',
                enabledCancellationFeeOverride: false,
            },
            pauseSubscriptionInfo: {
                allowedPlanIds: [],
                enabled: false,
            },
            freeWeekSubscriptionInfo: {
                allowedPlanIds: [],
                enabled: false,
            },
            startSubscriptionInfo: {
              enabled: false,
              message: '',
              allowedPlans: [],
            },
            subscribeEnabled: false,
            subscriptionSchedule: undefined,
            upgradeEnabled: false,
        },
      setupIntent: undefined,
        memberWorkoutHistory: [],
        memberExperiencePointsHistory: [],
        startSubscription: {
          plan: undefined,
          stripe: null,
          couponChecked: false,
          coupon: undefined,
          couponsForPlan: [],
          couponPlaceholder: '',
          isLoadingCoupons: false,
          consentChecked: false,
          paymentSummary: undefined,
        },
        buyMerchandise: {
          merchandise: [],
          error: null,
          invoice: undefined,
          isLoadingInvoice: false,
          coupons: [],
          selectedCoupon: undefined,
          isProcessingPayment: false,
          success: undefined,
          stripe: null,
          note: undefined,
          showConfirmActionInvoiceModal: false,
          showMerchandiseSuccessModal: false,
        },
    },
    view: {
        terminalStatus: 'disconnected',
        terminalError: null,
        error: null,
        isFetching: false,
        isFetchingExperiencePointsHistory: false,
        experiencePointsHistoryError: null,
        isLoadingTerminal: false,
        showAddPauseModal: false,
        showAddFreeWeeksModal: false,
        showCancelSubscriptionModal: false,
        showCancelPauseModal: false,
        showStartSubscriptionModal: false,
        validationError: null,
        successMessage: null,
        pauseEarliestEnd: moment().toISOString(),
        pausePlanId: '',
        pauseStartDate: moment().toISOString(),
        pauseEndDate: moment().toISOString(),
        isLoadingCancelSubscription: false,
        isLoadingStartSubscription: false,
        cancelSubscriptionError: null,
        startSubscriptionError: null,
        freeWeekEarliestEnd: moment().toISOString(),
        freeWeekPlanId: '',
        freeWeekStartDate: moment().toISOString(),
        freeWeekEndDate: moment().toISOString(),
        allowedStartSubscriptionPlans: [],
        showBuyMerchandiseModal: false,
        showStartSubscriptionSuccessModal: false,
        isLoadingBuyMerchandiseModal: false,
    },
};
